import React from 'react';

/**
 * Focus Management Utilities for Accessibility
 */

// Trap focus within a container (for modals, dropdowns, etc.)
export const trapFocus = (container) => {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleTabKey = (e) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  };

  container.addEventListener('keydown', handleTabKey);
  
  // Return cleanup function
  return () => {
    container.removeEventListener('keydown', handleTabKey);
  };
};

// Restore focus to previous element
export const restoreFocus = (previousActiveElement) => {
  if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
    previousActiveElement.focus();
  }
};

// Announce screen reader messages
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document.createElement('div');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    document.body.removeChild(announcement);
  }, 1000);
};

// Skip link component for keyboard navigation
export const SkipLink = ({ targetId, children = "Skip to main content" }) => {
  return (
    <a 
      href={`#${targetId}`}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-safir-primary text-white px-4 py-2 rounded-md z-50"
    >
      {children}
    </a>
  );
};

// Check if element is in viewport
export const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

// Scroll element into view with accessibility in mind
export const scrollIntoView = (element, options = {}) => {
  const defaultOptions = {
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
    ...options
  };

  element.scrollIntoView(defaultOptions);
  
  // Focus the element after scrolling
  if (typeof element.focus === 'function') {
    setTimeout(() => element.focus(), 500);
  }
};

// Generate unique IDs for accessibility
export const generateId = (prefix = 'id') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

// Keyboard navigation helpers
export const keyboardNavigation = {
  // Handle arrow key navigation
  arrowKeys: (callback) => (e) => {
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        callback('previous');
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        callback('next');
        break;
      case 'Home':
        e.preventDefault();
        callback('first');
        break;
      case 'End':
        e.preventDefault();
        callback('last');
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        callback('select');
        break;
      case 'Escape':
        e.preventDefault();
        callback('escape');
        break;
      default:
        break;
    }
  },

  // Handle menu navigation
  menuNavigation: (items, currentIndex, direction) => {
    let newIndex;
    
    switch (direction) {
      case 'next':
        newIndex = (currentIndex + 1) % items.length;
        break;
      case 'previous':
        newIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        break;
      case 'first':
        newIndex = 0;
        break;
      case 'last':
        newIndex = items.length - 1;
        break;
      default:
        return currentIndex;
    }
    
    // Focus the new item
    if (items[newIndex] && typeof items[newIndex].focus === 'function') {
      items[newIndex].focus();
    }
    
    return newIndex;
  }
};

// Color contrast checker (basic implementation)
export const checkColorContrast = (foreground, background) => {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Calculate relative luminance
  const getLuminance = (rgb) => {
    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const rgb1 = hexToRgb(foreground);
  const rgb2 = hexToRgb(background);
  
  if (!rgb1 || !rgb2) return null;

  const lum1 = getLuminance(rgb1);
  const lum2 = getLuminance(rgb2);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  return (brightest + 0.05) / (darkest + 0.05);
};

// ARIA attributes helper
export const ariaAttributes = {
  // Generate aria-label for dynamic content
  label: (content, context = '') => {
    return context ? `${content} - ${context}` : content;
  },

  // Generate aria-describedby
  describedBy: (...ids) => ids.filter(Boolean).join(' '),

  // Generate aria-labelledby
  labelledBy: (...ids) => ids.filter(Boolean).join(' '),

  // Status announcements
  status: (message) => ({
    'aria-live': 'polite',
    'aria-atomic': 'true',
    children: message
  }),

  // Alert announcements
  alert: (message) => ({
    'aria-live': 'assertive',
    'aria-atomic': 'true',
    'role': 'alert',
    children: message
  })
};
